import React, { useEffect, useMemo, useState } from 'react'
import { Button, InputNumber, Modal, Tooltip } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import styled from 'styled-components/macro'
import PurchaseOrderInput, {
  GuidanceText,
} from 'src/components/invoices/PurchaseOrderInput'
import { getInvoicePoSearchSupplierId } from 'src/utils/invoicePoSearchHelpers'
import {
  buildAllocationDraftRows,
  buildAllocationsRequestBody,
  createAllocationRow,
  formatAllocationPennies,
  getEditAllocationsActionMeta,
  MIN_ALLOCATION_ROWS,
  describeSavedSplitNextStep,
  summariseAllocationDraft,
  validateAllocationDraft,
} from 'src/utils/invoicePoAllocationHelpers'
import { saveInvoicePoAllocations } from 'src/features/NewInvoicesSlice'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'

/**
 * Multi-PO allocation: split one invoice total across several purchase
 * orders. The sum of the allocated amounts must equal the invoice total
 * exactly — express re-validates before saving.
 */
const AllocateInvoiceToPosModal = ({
  visible,
  venueId,
  invoice,
  seedSelections,
  onCancel,
  onAllocated,
}) => {
  const [rows, setRows] = useState([])
  const [saving, setSaving] = useState(false)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (visible && invoice) {
      setRows(buildAllocationDraftRows(invoice, seedSelections))
      setShowErrors(false)
    }
  }, [visible, invoice, seedSelections])

  const invoiceTotalPennies = invoice?.invoiceTotal ?? null

  const summary = useMemo(
    () => summariseAllocationDraft(rows, invoiceTotalPennies),
    [rows, invoiceTotalPennies],
  )

  const validation = useMemo(
    () => validateAllocationDraft(rows, invoiceTotalPennies),
    [rows, invoiceTotalPennies],
  )

  const updateRow = (key, update) => {
    setRows(prev =>
      prev.map(row => (row.key === key ? { ...row, ...update } : row)),
    )
  }

  const addRow = () => {
    setRows(prev => [...prev, createAllocationRow()])
  }

  const removeRow = key => {
    setRows(prev => prev.filter(row => row.key !== key))
  }

  const handleSave = async () => {
    const editMeta = getEditAllocationsActionMeta(invoice || {})
    if (!editMeta.canEdit) {
      RejectedNotification({
        message: 'Allocations locked',
        description:
          editMeta.blockReason ||
          'This invoice’s allocations can no longer be edited.',
      })
      return
    }

    if (!validation.valid) {
      setShowErrors(true)
      return
    }

    setSaving(true)
    try {
      const body = buildAllocationsRequestBody(rows)
      const response = await saveInvoicePoAllocations(
        invoice.id,
        body.allocations,
      )
      const savedAllocations =
        response?.allocations ||
        response?.data?.poAllocations ||
        body.allocations
      SuccessNotification({
        message: editMeta.isEdit ? 'Allocations updated' : 'Allocations saved',
        description: `${formatAllocationPennies(
          summary.allocatedPennies,
        )} allocated across ${
          body.allocations.length
        } purchase orders. ${describeSavedSplitNextStep(savedAllocations)}`,
      })
      onAllocated?.(response?.data || response)
    } catch (error) {
      const serverErrors = error?.response?.data?.data?.errors
      RejectedNotification({
        message: 'Could not save allocations',
        description:
          Array.isArray(serverErrors) && serverErrors.length
            ? serverErrors.map(row => row.message).join(' ')
            : error?.response?.data?.message ||
              error?.message ||
              'Please review the allocations and try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  const remaining = summary.remainingPennies
  const remainingTone =
    remaining == null
      ? 'neutral'
      : remaining === 0
      ? 'balanced'
      : remaining > 0
      ? 'under'
      : 'over'

  const editMeta = getEditAllocationsActionMeta(invoice || {})
  const isEdit = editMeta.isEdit

  return (
    <Modal
      title={
        isEdit
          ? 'Edit allocations across purchase orders'
          : 'Allocate invoice across purchase orders'
      }
      visible={visible}
      onCancel={saving ? undefined : onCancel}
      footer={null}
      destroyOnClose
      width={640}
    >
      {!invoice ? null : (
        <Body>
          <GuidanceText>
            {isEdit
              ? 'Fix how this invoice is split across purchase orders. You can edit while no allocated PO is reconciled or sent to Xero. Amounts must still add up to the invoice total exactly.'
              : 'This invoice covers more than one order. Select each purchase order and enter the portion of the invoice total that belongs to it. The amounts must add up to the invoice total exactly.'}
          </GuidanceText>

          <InvoiceSummaryRow>
            <div>
              <SummaryLabel>Invoice</SummaryLabel>
              <SummaryValue>
                {invoice.invoiceNumber || invoice.fileName || '—'}
              </SummaryValue>
            </div>
            <div>
              <SummaryLabel>Supplier</SummaryLabel>
              <SummaryValue>{invoice.supplierTitle || '—'}</SummaryValue>
            </div>
            <div>
              <SummaryLabel>Invoice total</SummaryLabel>
              <SummaryValue>
                {formatAllocationPennies(invoiceTotalPennies)}
              </SummaryValue>
            </div>
          </InvoiceSummaryRow>

          <RowsHeader>
            <RowsHeaderPo>Purchase order</RowsHeaderPo>
            <RowsHeaderAmount>Amount (£)</RowsHeaderAmount>
            <RowsHeaderSpacer />
          </RowsHeader>

          {rows.map(row => (
            <AllocationRow key={row.key}>
              <PoCell>
                <PurchaseOrderInput
                  venueId={venueId}
                  value={row.purchaseOrderNumber}
                  supplierId={getInvoicePoSearchSupplierId(invoice)}
                  invoiceDate={invoice?.invoiceDate}
                  invoiceTotalPennies={invoiceTotalPennies}
                  excludePurchaseOrderNumbers={rows
                    .map(item => item.purchaseOrderNumber)
                    .filter(Boolean)}
                  showOrderHints
                  placeholder='Search PO number'
                  onChange={update =>
                    updateRow(row.key, {
                      purchaseOrderNumber: update.purchaseOrderNumber || '',
                      orderId: update.linkedOrderId || null,
                    })
                  }
                />
              </PoCell>
              <AmountCell>
                <InputNumber
                  size='large'
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                  value={
                    row.amountPounds === '' ? null : Number(row.amountPounds)
                  }
                  placeholder='0.00'
                  onChange={value =>
                    updateRow(row.key, {
                      amountPounds: value == null ? '' : String(value),
                    })
                  }
                />
              </AmountCell>
              <RemoveCell>
                <Tooltip
                  title={
                    rows.length <= MIN_ALLOCATION_ROWS
                      ? `At least ${MIN_ALLOCATION_ROWS} purchase orders are needed`
                      : 'Remove this allocation'
                  }
                >
                  <Button
                    type='text'
                    icon={<DeleteOutlined />}
                    disabled={rows.length <= MIN_ALLOCATION_ROWS}
                    onClick={() => removeRow(row.key)}
                  />
                </Tooltip>
              </RemoveCell>
            </AllocationRow>
          ))}

          <AddRowButton type='dashed' icon={<PlusOutlined />} onClick={addRow}>
            Add another purchase order
          </AddRowButton>

          <TotalsBar tone={remainingTone}>
            <span>
              Allocated {formatAllocationPennies(summary.allocatedPennies)} of{' '}
              {formatAllocationPennies(invoiceTotalPennies)}
            </span>
            <strong>
              {remaining == null
                ? 'No invoice total'
                : remaining === 0
                ? 'Fully allocated'
                : remaining > 0
                ? `${formatAllocationPennies(remaining)} remaining`
                : `${formatAllocationPennies(Math.abs(remaining))} over`}
            </strong>
          </TotalsBar>

          {showErrors && !validation.valid && (
            <ErrorList>
              {validation.errors.map((message, index) => (
                <li key={index}>{message}</li>
              ))}
            </ErrorList>
          )}

          <FooterRow>
            <Button onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Tooltip
              title={
                validation.valid
                  ? ''
                  : 'Amounts must add up to the invoice total across at least two POs'
              }
            >
              <Button
                type='primary'
                loading={saving}
                disabled={!validation.valid && showErrors}
                onClick={handleSave}
              >
                {isEdit ? 'Save changes' : 'Save allocations'}
              </Button>
            </Tooltip>
          </FooterRow>
        </Body>
      )}
    </Modal>
  )
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const InvoiceSummaryRow = styled.div`
  display: flex;
  gap: 24px;
  padding: 12px;
  background: #fafafa;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
`

const SummaryLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.45);
`

const SummaryValue = styled.div`
  font-weight: 600;
`

const RowsHeader = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
`

const RowsHeaderPo = styled.div`
  flex: 1;
`

const RowsHeaderAmount = styled.div`
  width: 140px;
`

const RowsHeaderSpacer = styled.div`
  width: 32px;
`

const AllocationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const PoCell = styled.div`
  flex: 1;
`

const AmountCell = styled.div`
  width: 140px;
`

const RemoveCell = styled.div`
  width: 32px;
  display: flex;
  justify-content: center;
`

const AddRowButton = styled(Button)`
  && {
    width: fit-content;
  }
`

const TotalsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ tone }) =>
      tone === 'balanced'
        ? '#b7eb8f'
        : tone === 'over'
        ? '#ffa39e'
        : '#ffd591'};
  background: ${({ tone }) =>
    tone === 'balanced' ? '#f6ffed' : tone === 'over' ? '#fff1f0' : '#fff7e6'};
`

const ErrorList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #cf1322;
  font-size: 13px;

  li + li {
    margin-top: 4px;
  }
`

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`

export default AllocateInvoiceToPosModal
